import { useEffect, useRef, useState } from "react";
import { GIFEncoder, quantize, applyPalette } from "gifenc";
import { saveAs } from "file-saver";
import { useLogoStore } from "../state/logoStore";
import { PageLabel } from "../components/ui/PageLabel";

// === PAGE: GIF MAKER (upload video, trim, encode to animated GIF) ===

const FPS_OPTIONS = [5, 8, 10, 12, 15, 20, 24];
const WIDTH_OPTIONS = [180, 240, 320, 480, 640];

type Stage = "empty" | "loaded" | "encoding" | "done";

export function GifMakerPage() {
  const setPage = useLogoStore((s) => s.setPage);

  const [stage, setStage] = useState<Stage>("empty");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [fps, setFps] = useState(10);
  const [width, setWidth] = useState(320);
  const [loop, setLoop] = useState(true);
  const [progress, setProgress] = useState(0);
  const [gifUrl, setGifUrl] = useState<string | null>(null);
  const [gifSize, setGifSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState("clip");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cancelEncodeRef = useRef(false);

  // Cleanup object URLs on unmount or replace
  useEffect(() => () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (gifUrl) URL.revokeObjectURL(gifUrl);
  }, [videoUrl, gifUrl]);

  function handleFile(file: File) {
    if (!file.type.startsWith("video/")) {
      setError("That file isn't a video. Try .mp4, .mov, or .webm.");
      return;
    }
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (gifUrl) { URL.revokeObjectURL(gifUrl); setGifUrl(null); }
    setError(null);
    setFileName(file.name.replace(/\.[^.]+$/, "") || "clip");
    setVideoUrl(URL.createObjectURL(file));
    setStage("loaded");
  }

  function onVideoLoaded() {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    setNaturalSize({ w: v.videoWidth, h: v.videoHeight });
    setStart(0);
    setEnd(Math.min(v.duration, 5));
  }

  const aspect = naturalSize.h ? naturalSize.h / naturalSize.w : 0.5625;
  const targetH = Math.round(width * aspect);
  const frameCount = Math.max(1, Math.round((end - start) * fps));
  const estDurationMs = Math.round((end - start) * 1000);

  async function encodeGif() {
    const v = videoRef.current;
    if (!v) return;
    setError(null);
    setStage("encoding");
    setProgress(0);
    cancelEncodeRef.current = false;
    if (gifUrl) { URL.revokeObjectURL(gifUrl); setGifUrl(null); }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) { setError("Canvas not supported."); setStage("loaded"); return; }

    const gif = GIFEncoder();
    const delayMs = Math.round(1000 / fps);
    const total = frameCount;
    const wasMuted = v.muted;
    v.muted = true;

    try {
      for (let i = 0; i < total; i++) {
        if (cancelEncodeRef.current) {
          v.muted = wasMuted;
          setStage("loaded");
          return;
        }
        const t = start + (i / Math.max(1, total - 1)) * (end - start);
        await seekTo(v, t);
        ctx.drawImage(v, 0, 0, width, targetH);
        const { data } = ctx.getImageData(0, 0, width, targetH);
        const palette = quantize(data, 256, { format: "rgb444" });
        const index = applyPalette(data, palette, "rgb444");
        gif.writeFrame(index, width, targetH, { palette, delay: delayMs });
        setProgress(Math.round(((i + 1) / total) * 100));
        // Yield to UI so React renders the progress bar
        await new Promise((r) => setTimeout(r, 0));
      }
      gif.finish();
      const bytes = gif.bytes();
      // Native ArrayBuffer copy avoids SharedArrayBuffer typing issues
      const ab = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(ab).set(bytes);
      const blob = new Blob([ab], { type: "image/gif" });
      const url = URL.createObjectURL(blob);
      setGifUrl(url);
      setGifSize(blob.size);
      setStage("done");

      // Disable infinite loop if user opted out — gifenc loops forever by default.
      // (We still emit the file; loop=false is signaled by writing the looping
      // counter to a single play. gifenc's default loop=0 = forever; pass loop:1
      // for "play once" via finish options.)
    } catch (err) {
      console.error("GIF encode failed", err);
      setError("Encoding failed. Try a shorter clip or smaller size.");
      setStage("loaded");
    } finally {
      v.muted = wasMuted;
    }
  }

  function downloadGif() {
    if (!gifUrl) return;
    fetch(gifUrl)
      .then((r) => r.blob())
      .then((blob) => saveAs(blob, `${fileName}.gif`));
  }

  function reset() {
    cancelEncodeRef.current = true;
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (gifUrl) URL.revokeObjectURL(gifUrl);
    setVideoUrl(null);
    setGifUrl(null);
    setDuration(0);
    setStart(0);
    setEnd(0);
    setProgress(0);
    setStage("empty");
    setError(null);
  }

  return (
    <div className="page gif-maker">
      <PageLabel name="GIF MAKER" />
      <header className="topbar">
        <button className="btn pill-nav pill-nav-back" onClick={() => setPage("start")}>
          <span className="pill-nav-arrow" aria-hidden="true">←</span>
          <span>Home</span>
        </button>
      </header>

      {stage === "empty" ? (
        <GifHero onFile={handleFile} error={error} />
      ) : null}

      <div style={{ padding: "20px 24px 80px", maxWidth: 1080, margin: "0 auto" }}>
        {error && stage !== "empty" && (
          <div style={{
            background: "rgba(220, 38, 38, 0.18)",
            color: "#fff",
            border: "1px solid rgba(220, 38, 38, 0.40)",
            padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13,
          }}>{error}</div>
        )}

        {stage !== "empty" && videoUrl && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {/* LEFT: source video + controls */}
            <div className="gm-card">
              <h3 style={{ margin: "0 0 12px" }}>Source video</h3>
              <video
                ref={videoRef}
                src={videoUrl}
                onLoadedMetadata={onVideoLoaded}
                controls
                muted
                style={{ width: "100%", borderRadius: 12, background: "#000" }}
              />
              {duration > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Slider
                    label={`Trim start · ${start.toFixed(2)}s`}
                    min={0} max={duration} step={0.05}
                    value={start}
                    onChange={(v) => setStart(Math.min(v, end - 0.1))}
                  />
                  <Slider
                    label={`Trim end · ${end.toFixed(2)}s`}
                    min={0} max={duration} step={0.05}
                    value={end}
                    onChange={(v) => setEnd(Math.max(v, start + 0.1))}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                    <Select
                      label="Frames per second"
                      value={fps}
                      onChange={(v) => setFps(Number(v))}
                      options={FPS_OPTIONS.map((n) => ({ value: n, label: `${n} fps` }))}
                    />
                    <Select
                      label="Width"
                      value={width}
                      onChange={(v) => setWidth(Number(v))}
                      options={WIDTH_OPTIONS.map((n) => ({ value: n, label: `${n} px` }))}
                    />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13 }}>
                    <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
                    Loop forever
                  </label>
                  <div style={{ marginTop: 14, fontSize: 12, color: "rgba(255,255,255,0.78)" }}>
                    {frameCount} frames · {width}×{targetH} · ≈ {(estDurationMs / 1000).toFixed(2)}s
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                    <button className="btn primary big" onClick={encodeGif} disabled={stage === "encoding" || end <= start}>
                      {stage === "encoding" ? `Encoding… ${progress}%` : "🎞 Make GIF"}
                    </button>
                    <button className="btn ghost" onClick={reset}>Replace video</button>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: GIF preview */}
            <div className="gm-card">
              <h3 style={{ margin: "0 0 12px" }}>Output</h3>
              {stage === "encoding" && (
                <div style={{ display: "grid", placeItems: "center", height: 240, color: "rgba(255,255,255,0.78)" }}>
                  <div>
                    <div style={{ fontSize: 14, marginBottom: 8 }}>Encoding {progress}%…</div>
                    <div style={{ width: 240, height: 6, background: "rgba(255,255,255,0.18)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ width: `${progress}%`, height: "100%", background: "var(--primary, #3D6AE1)", transition: "width .15s" }} />
                    </div>
                  </div>
                </div>
              )}
              {stage === "done" && gifUrl && (
                <>
                  <img src={gifUrl} alt="GIF preview" style={{ width: "100%", borderRadius: 12, background: "#fff", border: "1px solid var(--border)" }} />
                  <div style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.78)" }}>
                    {(gifSize / 1024).toFixed(0)} KB · {width}×{targetH} · {fps} fps
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button className="btn primary big" onClick={downloadGif}>⬇ Download .gif</button>
                    <button className="btn" onClick={() => setStage("loaded")}>Re-encode</button>
                  </div>
                </>
              )}
              {stage === "loaded" && (
                <div style={{ display: "grid", placeItems: "center", height: 240, color: "rgba(255,255,255,0.65)", fontSize: 13, textAlign: "center", padding: "0 16px" }}>
                  Trim and adjust on the left, then click <strong>Make GIF</strong>. Output appears here.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========== Helpers ========== */

function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      resolve();
    };
    video.addEventListener("seeked", onSeeked);
    // Clamp to a hair below duration to avoid the browser refusing to seek past the end
    video.currentTime = Math.min(time, Math.max(0, video.duration - 0.001));
  });
}

function GifHero({ onFile, error }: { onFile: (file: File) => void; error: string | null }) {
  const [hover, setHover] = useState(false);
  return (
    <div className="gm-hero-shell">
      <div className="gm-hero">
        <h1 className="gm-hero-title">
          <span className="gm-hero-brand">LOGOMAN</span>
          <span className="gm-hero-dot">.</span>
          <span className="gm-hero-pro">pro</span>
          <span className="gm-hero-light"> GIF Maker</span>
        </h1>
        <p className="gm-hero-lede">
          Upload your video and turn a moment into an animated GIF.<br />
          Trim, resize, and export in seconds.
        </p>

        {error && <div className="gm-hero-error">{error}</div>}

        <div className="gm-hero-grid">
          {/* Card 1: file picker */}
          <label className="gm-glass-card gm-upload-card">
            <input
              type="file"
              accept="video/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
            />
            <div className="gm-upload-icon gm-upload-icon-gif">
              <svg viewBox="0 0 64 64" aria-hidden="true">
                <rect x="9" y="14" width="46" height="36" rx="7" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.95)" strokeWidth="2.5"/>
                <text x="32" y="38" textAnchor="middle" fontSize="14" fontWeight="900" fill="#fff" fontFamily="Roboto, system-ui, sans-serif">GIF</text>
              </svg>
            </div>
            <h3>Choose File</h3>
            <p>Upload a GIF, MP4, MOV, or WebM</p>
            <span className="gm-cta">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14"/>
              </svg>
              Choose File
            </span>
          </label>

          {/* Card 2: drag & drop */}
          <div
            className={`gm-glass-card gm-drop-card${hover ? " is-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setHover(true); }}
            onDragLeave={() => setHover(false)}
            onDrop={(e) => {
              e.preventDefault();
              setHover(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}
          >
            <div className="gm-upload-icon gm-upload-icon-drop">
              <svg viewBox="0 0 64 64" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 16h18a4 4 0 0 1 4 4v22a8 8 0 0 1-8 8H24a8 8 0 0 1-8-8V20a4 4 0 0 1 4-4z"/>
                <path d="M38 16v8h8"/>
              </svg>
            </div>
            <h3>Drag &amp; Drop</h3>
            <p>Drop your clip anywhere on this card</p>
            <span className="gm-cta gm-cta-secondary">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 16V4m0 12l-4-4m4 4l4-4M5 20h14"/>
              </svg>
              Drop here
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Slider(props: {
  label: string; min: number; max: number; step: number;
  value: number; onChange: (v: number) => void;
}) {
  return (
    <label style={{ display: "block", marginTop: 10 }}>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", marginBottom: 4 }}>{props.label}</div>
      <input
        type="range"
        min={props.min} max={props.max} step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
        style={{ width: "100%" }}
      />
    </label>
  );
}

function Select<T extends string | number>(props: {
  label: string; value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", marginBottom: 4 }}>{props.label}</div>
      <select
        value={String(props.value)}
        onChange={(e) => {
          const raw = e.target.value;
          const matched = props.options.find((o) => String(o.value) === raw);
          if (matched) props.onChange(matched.value);
        }}
        style={{
          width: "100%", padding: "8px 10px", borderRadius: 8,
          border: "1px solid var(--border, #e2e8f0)", fontSize: 13, background: "#fff",
        }}
      >
        {props.options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

